<template>
  <div class="triangle-properties-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-tri">
                <feGaussianBlur stdDeviation="3" result="blueBlur"/>
                <feMerge><feMergeNode in="blueBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
           </defs>

           <!-- Grid -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Mode: right (Pythagorean Theorem) -->
           <g v-if="mode === 'right'" transform="translate(300, 300)">
              <!-- Squares representing area -->
              <!-- a^2 side -->
              <rect :x="-a" y="0" :width="a" :height="a" fill="rgba(239, 68, 68, 0.2)" stroke="#ef4444" stroke-width="2" stroke-dasharray="4" />
              <text :x="-a/2" :y="a/2 + 5" fill="#fca5a5" font-size="20" text-anchor="middle" font-weight="900">a²</text>
              
              <!-- b^2 side -->
              <rect x="0" :y="a" :width="b" :height="b" fill="rgba(59, 130, 246, 0.2)" stroke="#3b82f6" stroke-width="2" stroke-dasharray="4" />
              <text :x="b/2" :y="a + b/2 + 10" fill="#93c5fd" font-size="20" text-anchor="middle" font-weight="900">b²</text>

              <!-- c^2 side (Hypotenuse) -->
              <g :transform="`rotate(${angleC})`">
                 <rect x="0" :y="-c" :width="c" :height="c" fill="rgba(16, 185, 129, 0.2)" stroke="#10b981" stroke-width="3" />
                 <text :x="c/2" :y="-c/2 + 10" fill="#6ee7b7" font-size="28" text-anchor="middle" font-weight="900" filter="url(#glow-tri)">c²</text>
              </g>

              <!-- The Triangle -->
              <path :d="`M 0 0 L 0 ${a} L ${b} ${a} Z`" fill="rgba(255, 255, 255, 0.05)" stroke="#fff" stroke-width="4" stroke-linejoin="round" />
              
              <!-- Right angle symbol -->
              <path d="M 0 20 L 20 20 L 20 0" fill="none" stroke="#fff" stroke-width="2" :transform="`translate(0, ${a-20})`" />
           </g>

           <!-- Mode: isosceles (Isosceles Properties) -->
           <g v-if="mode === 'isosceles'" transform="translate(400, 150)">
              <path :d="`M 0 0 L ${-iBase/2} ${iHeight} L ${iBase/2} ${iHeight} Z`" fill="rgba(59, 130, 246, 0.1)" stroke="#3b82f6" stroke-width="4" filter="url(#glow-tri)" />
              
              <!-- Symmetry line (Three-lines-in-one) -->
              <line x1="0" y1="0" x2="0" :y2="iHeight" stroke="#ef4444" stroke-width="2" stroke-dasharray="8,4" />
              <circle cx="0" :cy="iHeight" r="5" fill="#ef4444" />
              <text x="10" :y="iHeight/2" fill="#fca5a5" font-size="14">三线合一</text>

              <!-- Angles -->
              <path :d="`M ${-iBase/2 + 40} ${iHeight} A 40 40 0 0 0 ${-iBase/2 + 40*Math.cos(0.8)} ${iHeight - 40*Math.sin(0.8)}`" fill="none" stroke="#ffd700" stroke-width="3" />
              <path :d="`M ${iBase/2 - 40} ${iHeight} A 40 40 0 0 1 ${iBase/2 - 40*Math.cos(0.8)} ${iHeight - 40*Math.sin(0.8)}`" fill="none" stroke="#ffd700" stroke-width="3" />
              
              <text :x="-iBase/2 + 45" :y="iHeight - 15" fill="#fbbf24" font-size="16" font-weight="bold">∠B</text>
              <text :x="iBase/2 - 75" :y="iHeight - 15" fill="#fbbf24" font-size="16" font-weight="bold">∠C</text>
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>当前模式</label>
              <span class="val">{{ mode === 'right' ? '勾股论证' : '等腰性质' }}</span>
           </div>
           <div class="hud-item cyan">
              <label>几何判定</label>
              <span class="val">{{ isSatisfied ? '判定成立' : '动态平衡' }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item" v-if="mode === 'right'">
                 <label>直角边 a ({{ (a/50).toFixed(1) }})</label>
                 <el-slider v-model="a" :min="40" :max="150" size="small"></el-slider>
              </div>
              <div class="s-item" v-if="mode === 'right'">
                 <label>直角边 b ({{ (b/50).toFixed(1) }})</label>
                 <el-slider v-model="b" :min="40" :max="200" size="small"></el-slider>
              </div>
              <div class="s-item" v-if="mode === 'isosceles'">
                 <label>顶角调节 ({{ (iBase/4).toFixed(0) }}°)</label>
                 <el-slider v-model="iBase" :min="40" :max="300" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>切换类型</label>
                 <el-radio-group v-model="mode" size="mini">
                   <el-radio-button label="right">勾股</el-radio-button>
                   <el-radio-button label="isosceles">等腰</el-radio-button>
                 </el-radio-group>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p><strong>几何本质：</strong>{{ modeDesc }}。{{ tipText }}</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrianglePropertiesViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'right',
      a: 100,
      b: 150,
      iBase: 160,
      iHeight: 250
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    c() { return Math.sqrt(this.a**2 + this.b**2); },
    angleC() { return Math.atan2(this.a, this.b) * (180 / Math.PI); },
    topicIntro() {
       return this.mode === 'right' ? '勾股定理：直角三角形边长的平方和规律' : '等腰性质：底角相等与三线合一的对称美';
    },
    activeFormula() {
       return this.mode === 'right' ? 'a² + b² = c²' : 'AB = AC ⇔ ∠B = ∠C';
    },
    modeDesc() {
       return this.mode === 'right' 
        ? '直角三角形两直角边的平方（红、蓝正方形面积）之和，恒等于斜边的平方（绿正方形面积）'
        : '等腰三角形不仅底角相等，其顶角平分线、底边中线、底边高线更是三线合一';
    },
    tipText() {
       return '实时调整边长，观察面积正方形如何随之扩张。';
    },
    isSatisfied() { return true; }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/勾股|直角|三边/.test(kp)) this.mode = 'right';
      else if (/等腰|判/.test(kp)) this.mode = 'isosceles';
      else this.mode = 'right';
    }
  }
}
</script>

<style scoped lang="scss">
.triangle-properties-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 380px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.blue { border-left: 3px solid #3b82f6; } &.cyan { border-left: 3px solid #10b981; } } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; margin-bottom: 2px; } } }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px; i { color: #16a34a; font-size: 1.1rem; } .discovery-content { flex: 1; p { margin: 0; font-size: 12px; color: #166534; line-height: 1.5; strong { color: #14532d; } } } }
</style>
